<template>
  <div class="conversation-history-details" :class="{ selected: isCurrentConversation }" @click="goToConversation">
    <div class="conversation-history-details__content">
      <div class="conversation-name" :class="{ 'selected-text': isCurrentConversation }">
        {{ title }}
      </div>
      <div class="creation-date">
        {{ formatCreatedAt }}
      </div>
    </div>

    <div class="conversation-actions-menu-wrapper" data-app @click.stop>
      <BbMenu
        v-model="showActionsMenu"
        class="conversation-actions-menu"
        display-items="6"
        max-width="200px"
        min-width="200px"
        nudge-bottom="8"
        size="medium"
        is-offset-y
        alignment="bottom-left"
      >
        <template #menuActivator>
          <BbBtn
            :class="['open-actions-menu-button', { active: showActionsMenu }]"
            size="x-small"
            type="icon"
            icon="menu-dots"
            icon-size="medium"
            icon-type="filled"
            no-background
          ></BbBtn>
        </template>

        <BbMenuItem class="action-item" size="medium" @click="onShareConversation">
          <div class="action-item__content">
            <BbIcon size="small" name="share" />
            <TextBody i18n-key="common.share"></TextBody>
          </div>
        </BbMenuItem>
      </BbMenu>
    </div>

    <ShareConversationPopup v-model="shareConversationPopupOpened" :conversation="conversation" />
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router/dist/vue-router';

import { calculatePassedTime } from '~/utils/date-utils';
import { Routes } from '~/constants/routes';
import { GetCurrentConversation } from '~/constants/getters';
import useConversations from '~/composables/useConversations';
import { useI18n } from 'vue-i18n';
import TextBody from '~/components/common/TextBody.vue';
import ShareConversationPopup from '~/components/conversation/ShareConversationPopup.vue';

export default {
  components: { TextBody, ShareConversationPopup },
  props: {
    conversation: {
      type: Object,
      default: () => {},
    },
  },

  setup(props) {
    const router = useRouter();
    const store = useStore();
    const { t } = useI18n();
    const { setConversationScope } = useConversations();

    const currentConversation = computed(() => store.getters[`conversationStore/${GetCurrentConversation}`]);
    const isCurrentConversation = computed(
      () => props.conversation.conversationId === currentConversation.value.conversationId,
    );
    const actualConversation = computed(() => props.conversation);
    const formatCreatedAt = computed(() => calculatePassedTime(actualConversation.value.timestamp));
    const title = computed(() => props.conversation.title || formatCreatedAt.value);
    const conversationName = computed(() => actualConversation.value.conversationId || t('conversation.conversation'));
    const goToConversation = () => {
      const breadcrumb = [];
      // reinitialise scope of the the next conversation
      setConversationScope({ isScoped: false, collection: {}, breadcrumb });
      router.push({ name: Routes.Conversation.name, params: { conversationId: props.conversation.conversationId } });
    };

    const showActionsMenu = ref(false);
    const shareConversationPopupOpened = ref(false);
    const onShareConversation = () => {
      showActionsMenu.value = false;
      shareConversationPopupOpened.value = true;
    };

    return {
      conversationName,
      formatCreatedAt,
      isCurrentConversation,
      goToConversation,
      showActionsMenu,
      shareConversationPopupOpened,
      onShareConversation,
      title,
    };
  },
};
</script>

<style scoped lang="scss">
.conversation-history-details:hover {
  background-color: $bb-neutral-4;
}

.selected {
  background-color: $bb-neutral-1 !important;
}

.selected-text {
  color: $bb-neutral-0 !important;
}

.conversation-history-details {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: $bb-spacing-2;
  transition: background-color 0.3s ease;
  cursor: pointer;
  padding: $bb-spacing-2 $bb-spacing-4;
  border-radius: 100px;
  background: $bb-neutral-6;
  margin-bottom: $bb-spacing-1;

  &__content {
    min-width: 0;
    flex: 1;
  }

  .conversation-name {
    @include bb-medium-text-styles('md');
    line-height: $bb-spacing-5;
    color: $bb-neutral-1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .creation-date {
    @include bb-medium-text-styles('xs');
    color: $bb-neutral-3;
  }

  .conversation-actions-menu-wrapper {
    flex-shrink: 0;

    :deep(.open-actions-menu-button) {
      opacity: 0;

      &.active {
        opacity: 1;
      }
    }
  }

  &:hover .conversation-actions-menu-wrapper :deep(.open-actions-menu-button) {
    opacity: 1;
  }
}

.action-item {
  color: $bb-primary-1 !important;

  &__content {
    display: flex;
    align-items: center;
    gap: $bb-spacing-2;
  }
}
</style>
